import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  setDoc,
  deleteDoc, 
  query, 
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  serverTimestamp
} from 'firebase/firestore';
import { getStorage, ref, deleteObject, uploadBytes, getDownloadURL } from 'firebase/storage';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { 
  PropertyReportModel, 
  PropertyEntryModel, 
  DocumentContent, 
  Company, 
  CompanyMember, 
  CompanyJoinRequest, 
  SharedReport, 
  UserProfile, 
  CompanyRole, 
  RequestStatus,
  SharingInfo
} from '../types/models';
import { serializeFirebaseDocument } from '../utils/firebaseSerializer';
import { preprocessImageUrl } from '../utils/imageUtils';

export class FirebaseService {
  // Helper method to ensure user is authenticated
  private static async ensureAuthenticated(): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to access Firebase');
    }
    
    // Wait for auth token to be refreshed if needed
    try {
      await auth.currentUser.getIdToken(false);
    } catch (error) {
      // console.warn('Auth token refresh failed, attempting force refresh...');
      await auth.currentUser.getIdToken(true);
    }
  }

  private static getCurrentUserId(): string | null {
    // This should be integrated with your auth context
    // For now, we'll assume the user ID comes from somewhere else
    return null; // Replace with actual user ID from auth context
  }

  // Helper to handle Firebase errors with user-friendly messages
  private static handleFirebaseError(error: any, operation: string): never {
    // console.error(`Error during ${operation}:`, error);
    
    // Check for permission denied errors
    if (error.code === 'permission-denied' || error.message?.includes('permission-denied')) {
      throw new Error('You do not have permission to perform this action. Please ensure you are a member of the company.');
    }
    
    // Check for authentication errors
    if (error.code === 'unauthenticated' || error.message?.includes('unauthenticated')) {
      throw new Error('You must be logged in to perform this action.');
    }
    
    // Check for not found errors
    if (error.code === 'not-found') {
      throw new Error('The requested resource was not found.');
    }
    
    // If error already has a user-friendly message, use it
    if (error instanceof Error && error.message && !error.message.includes('Firebase')) {
      throw error;
    }
    
    // Generic error with operation context
    throw new Error(`Failed to ${operation}. ${error.message || 'Please try again.'}`);
  }

  // MARK: - Reports
  static async fetchReports(userId: string): Promise<PropertyReportModel[]> {
    try {
      // Ensure user is properly authenticated before making the request
      await this.ensureAuthenticated();
      
      const reportsQuery = query(
        collection(db, `users/${userId}/reports`)
        // Note: orderBy might require index - uncomment when ready
        // orderBy('created_at', 'desc')
      );

      const querySnapshot = await getDocs(reportsQuery);
      const reports: PropertyReportModel[] = [];

      querySnapshot.forEach((document) => {
        const data = document.data();
        const serializedData = serializeFirebaseDocument(data);
        reports.push({
          id: document.id,
          title: serializedData.title,
          createdAt: new Date(serializedData.created_at),
          updatedAt: new Date(serializedData.updated_at || serializedData.created_at),
          entries: serializedData.entries || []
        });
      });

      // Sort in memory instead of using Firestore orderBy (avoids index requirement)
      return reports.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error: any) {
      // console.error('🔥 Error fetching reports:', error);
      
      // If we get a permissions error, return empty array
      if (error.code === 'permission-denied' || error.message.includes('permission')) {
        return [];
      }
      
      throw error;
    }
  }

  static async createReport(userId: string, title: string): Promise<string> {
    const now = new Date();
    const report = {
      title,
      created_at: Timestamp.fromDate(now),
      updated_at: Timestamp.fromDate(now),
      entries: []
    };

    const docRef = await addDoc(collection(db, `users/${userId}/reports`), report);
    return docRef.id;
  }

  static async updateReportTitle(userId: string, reportId: string, newTitle: string): Promise<void> {
    const reportRef = doc(db, `users/${userId}/reports`, reportId);
    await updateDoc(reportRef, {
      title: newTitle,
      updated_at: Timestamp.fromDate(new Date())
    });
  }

  static async deleteReport(userId: string, reportId: string): Promise<void> {
    await this.ensureAuthenticated();
    
    // console.log('🗑️ Starting deletion of report:', reportId);
    
    // First get all entries for this report to extract image URLs
    const entriesQuery = query(
      collection(db, `users/${userId}/entries`),
      where('report_id', '==', reportId)
    );
    
    const entriesSnapshot = await getDocs(entriesQuery);
    // console.log('🗑️ Found', entriesSnapshot.docs.length, 'entries to process');
    
    // Collect all image URLs that need to be deleted from Storage
    const imageUrlsToDelete: string[] = [];
    
    entriesSnapshot.docs.forEach(entryDoc => {
      const entryData = entryDoc.data();
      
      // Handle single image (backward compatibility)
      if (entryData.photo_url) {
        imageUrlsToDelete.push(entryData.photo_url);
      }
      
      // Handle multiple images
      if (entryData.images && Array.isArray(entryData.images)) {
        entryData.images.forEach((image: any) => {
          if (image.photo_url) {
            imageUrlsToDelete.push(image.photo_url);
          }
        });
      }
    });
    
    // console.log('🗑️ Found', imageUrlsToDelete.length, 'images to delete from Storage');
    
    // Delete images from Firebase Storage
    const storage = getStorage();
    const imageDeletePromises = imageUrlsToDelete.map(async (imageUrl) => {
      try {
        // Extract the storage path from the Firebase Storage URL
        const storagePath = this.extractStoragePathFromUrl(imageUrl);
        if (storagePath) {
          const imageRef = ref(storage, storagePath);
          await deleteObject(imageRef);
          // console.log('🗑️ Deleted image from storage:', storagePath);
        }
      } catch (error) {
        // console.error('🗑️ Failed to delete image from storage:', imageUrl, error);
        // Continue with other deletions even if one fails
      }
    });
    
    // Delete all entries from Firestore
    const entryDeletePromises = entriesSnapshot.docs.map(entryDoc => 
      deleteDoc(doc(db, `users/${userId}/entries`, entryDoc.id))
    );
    
    // Execute all deletions in parallel
    await Promise.all([
      ...imageDeletePromises,
      ...entryDeletePromises
    ]);
    
    // console.log('🗑️ All entries and images deleted');

    // Finally, delete the report document
    await deleteDoc(doc(db, `users/${userId}/reports`, reportId));
    // console.log('🗑️ Report deleted successfully');
  }

  /**
   * Extract the storage path from a Firebase Storage URL
   */
  private static extractStoragePathFromUrl(url: string): string | null {
    try {
      // Firebase Storage URLs have this format:
      // https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media&token={token}
      const urlParts = url.split('/o/')[1];
      if (urlParts) {
        const pathPart = urlParts.split('?')[0];
        return decodeURIComponent(pathPart);
      }
      return null;
    } catch (error) {
      // console.error('Error extracting storage path from URL:', url, error);
      return null;
    }
  }

  /**
   * Upload external image file to Firebase Storage and return the download URL
   */
  static async uploadExternalImage(dataURL: string, fileName: string, reportId: string): Promise<string> {
    try {
      await this.ensureAuthenticated();
      
      // Convert data URL to blob
      const response = await fetch(dataURL);
      const blob = await response.blob();
      
      const storage = getStorage();
      const timestamp = Date.now();
      const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
      const imageId = `${timestamp}_${sanitizedFileName}`;
      const storagePath = `users/${auth.currentUser!.uid}/images/${imageId}`;
      const storageRef = ref(storage, storagePath);
      
      // console.log('📤 Uploading external image to storage:', storagePath);
      const uploadResult = await uploadBytes(storageRef, blob);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      return downloadURL;
    } catch (error) {
      // console.error('❌ Error uploading external image:', error);
      throw error;
    }
  }

  // Get single report by ID
  static async getReport(reportId: string, userId: string): Promise<PropertyReportModel | null> {
    if (!userId) {
      throw new Error('User ID is required to fetch report');
    }
    
    try {
      // Ensure user is properly authenticated before making the request
      await this.ensureAuthenticated();
      
      const reportRef = doc(db, `users/${userId}/reports`, reportId);
      // console.log('🔥 Attempting to fetch from path:', `users/${userId}/reports/${reportId}`);
      
      const reportDoc = await getDoc(reportRef);
      
      if (!reportDoc.exists()) {
        // If the document doesn't exist, create a fallback report
        const fallbackReport: PropertyReportModel = {
          id: reportId,
          title: `Report ${reportId}`,
          createdAt: new Date(),
          updatedAt: new Date(),
          entries: []
        };
        
        return fallbackReport;
      }
      
      const data = reportDoc.data();
      
      const serializedData = serializeFirebaseDocument(data);
      return {
        id: reportDoc.id,
        title: serializedData.title,
        createdAt: new Date(serializedData.created_at),
        updatedAt: new Date(serializedData.updated_at || serializedData.created_at),
        entries: serializedData.entries || []
      };
    } catch (error: any) {
      // console.error('🔥 Firebase access error details:', error);
      
      // If we get a permissions error, create a fallback report
      if (error.code === 'permission-denied' || error.message.includes('permission')) {
        const fallbackReport: PropertyReportModel = {
          id: reportId,
          title: `Report ${reportId} (Offline Mode)`,
          createdAt: new Date(),
          updatedAt: new Date(),
          entries: []
        };
        
        return fallbackReport;
      }
      
      throw error;
    }
  }

  // Get entries for a specific report
  static async getEntriesForReport(reportId: string, userId: string): Promise<PropertyEntryModel[]> {
    
    return this.fetchEntries(userId, reportId);
  }

  // MARK: - Entries
  static async fetchEntries(userId: string, reportId: string): Promise<PropertyEntryModel[]> {
    if (!userId) {
      throw new Error('User ID is required to fetch entries');
    }
    
    try {
      // Ensure user is properly authenticated before making the request
      await this.ensureAuthenticated();
      
      const entriesQuery = query(
        collection(db, `users/${userId}/entries`),
        where('report_id', '==', reportId)
        // Note: orderBy might require index - uncomment when ready  
        // orderBy('created_at', 'asc')
      );

      // console.log('🔥 Querying entries from path:', `users/${userId}/entries`);
      const querySnapshot = await getDocs(entriesQuery);
      const entries: PropertyEntryModel[] = [];

      querySnapshot.forEach((document) => {
        const data = document.data();
        const serializedData = serializeFirebaseDocument(data);
        
        entries.push({
          id: document.id,
          title: serializedData.title,
          note: serializedData.note,
          createdAt: new Date(serializedData.created_at),
          reportId: serializedData.report_id,
          latitude: serializedData.latitude,
          longitude: serializedData.longitude,
          address: serializedData.address,
          images: (serializedData.images || []).map((img: any) => ({
            id: img.id,
            photoURL: preprocessImageUrl(img.photo_url), // Map from snake_case to camelCase and clean URL
            metadata: {
              capturedAt: img.metadata?.captured_at || new Date().toISOString(), // Keep as string
              latitude: img.metadata?.latitude,
              longitude: img.metadata?.longitude,
              address: img.metadata?.address,
            }
          })),
          // Backward compatibility
          photoURL: serializedData.photo_url,
          imageMetadata: serializedData.image_metadata
        });
      });

      // Sort in memory
      return entries.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
      
    } catch (error: any) {
      // console.error('🔥 Error fetching entries:', error);
      
      // If we get a permissions error, return empty array
      if (error.code === 'permission-denied' || error.message.includes('permission')) {
        return [];
      }
      
      throw error;
    }
  }

  static async createEntry(
    userId: string, 
    entryData: Omit<PropertyEntryModel, 'id' | 'createdAt'>
  ): Promise<string> {
    const entry = {
      title: entryData.title,
      note: entryData.note,
      created_at: Timestamp.fromDate(new Date()),
      report_id: entryData.reportId,
      latitude: entryData.latitude,
      longitude: entryData.longitude,
      address: entryData.address,
      images: entryData.images,
      // Backward compatibility
      photo_url: entryData.photoURL,
      image_metadata: entryData.imageMetadata
    };

    const docRef = await addDoc(collection(db, `users/${userId}/entries`), entry);
    return docRef.id;
  }

  static async updateEntry(
    userId: string, 
    entryId: string, 
    updateData: Partial<Omit<PropertyEntryModel, 'id' | 'createdAt' | 'reportId'>>
  ): Promise<void> {
    const entryRef = doc(db, `users/${userId}/entries`, entryId);
    
    const firestoreUpdateData: any = {};
    
    if (updateData.title !== undefined) firestoreUpdateData.title = updateData.title;
    if (updateData.note !== undefined) firestoreUpdateData.note = updateData.note;
    if (updateData.latitude !== undefined) firestoreUpdateData.latitude = updateData.latitude;
    if (updateData.longitude !== undefined) firestoreUpdateData.longitude = updateData.longitude;
    if (updateData.address !== undefined) firestoreUpdateData.address = updateData.address;
    if (updateData.images !== undefined) firestoreUpdateData.images = updateData.images;
    if (updateData.photoURL !== undefined) firestoreUpdateData.photo_url = updateData.photoURL;
    if (updateData.imageMetadata !== undefined) firestoreUpdateData.image_metadata = updateData.imageMetadata;

    await updateDoc(entryRef, firestoreUpdateData);
  }

  static async deleteEntry(userId: string, entryId: string): Promise<void> {
    await deleteDoc(doc(db, `users/${userId}/entries`, entryId));
  }

  // MARK: - Utility Functions
  static async addEntryToReport(userId: string, reportId: string, entryId: string): Promise<void> {
    // Fetch current report
    const reportRef = doc(db, `users/${userId}/reports`, reportId);
    const reportDoc = await getDocs(query(collection(db, `users/${userId}/reports`), where('__name__', '==', reportId)));
    
    if (!reportDoc.empty) {
      const reportData = reportDoc.docs[0].data();
      const currentEntries = reportData.entries || [];
      
      if (!currentEntries.includes(entryId)) {
        await updateDoc(reportRef, {
          entries: [...currentEntries, entryId],
          updated_at: Timestamp.fromDate(new Date())
        });
      }
    }
  }

  static async removeEntryFromReport(userId: string, reportId: string, entryId: string): Promise<void> {
    const reportRef = doc(db, `users/${userId}/reports`, reportId);
    const reportDoc = await getDocs(query(collection(db, `users/${userId}/reports`), where('__name__', '==', reportId)));
    
    if (!reportDoc.empty) {
      const reportData = reportDoc.docs[0].data();
      const currentEntries = reportData.entries || [];
      
      await updateDoc(reportRef, {
        entries: currentEntries.filter((id: string) => id !== entryId),
        updated_at: Timestamp.fromDate(new Date())
      });
    }
  }

  // MARK: - Document Content Management
  static async saveDocumentContent(documentContent: DocumentContent): Promise<void> {
    // console.log('🔥 Saving document content for report:', documentContent.reportId);
    // console.log('📋 Pages to save:', documentContent.pages);
    
    try {
      await this.ensureAuthenticated();
      
      const docRef = doc(db, `users/${documentContent.userId}/documentContent/${documentContent.reportId}`);
      
      await updateDoc(docRef, {
        pages: documentContent.pages,
        lastModified: Timestamp.fromDate(documentContent.lastModified),
        reportId: documentContent.reportId,
        userId: documentContent.userId
      }).catch(async (error) => {
        if (error.code === 'not-found') {
          // Document doesn't exist, create it with the specific reportId
          await setDoc(docRef, {
            pages: documentContent.pages,
            lastModified: Timestamp.fromDate(documentContent.lastModified),
            reportId: documentContent.reportId,
            userId: documentContent.userId
          });
        } else {
          throw error;
        }
      });
      
      // console.log('✅ Document content saved successfully with', documentContent.pages.length, 'pages');
    } catch (error) {
      // console.error('❌ Error saving document content:', error);
      throw error;
    }
  }

  static async loadDocumentContent(reportId: string, userId: string): Promise<DocumentContent | null> {
    try {
      await this.ensureAuthenticated();
      
      const docRef = doc(db, `users/${userId}/documentContent/${reportId}`);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const serializedData = serializeFirebaseDocument(data);
        
        const documentContent = {
          reportId: serializedData.reportId,
          pages: serializedData.pages || [''],
          lastModified: serializedData.lastModified || new Date(),
          userId: serializedData.userId
        };
        
        return documentContent;
      } else {
        return null;
      }
    } catch (error) {
      // console.error('❌ Error loading document content:', error);
      return null;
    }
  }

  static async loadSharedDocumentContent(reportId: string): Promise<DocumentContent | null> {
    try {
      await this.ensureAuthenticated();
      
      const profile = await this.fetchUserProfile();
      if (!profile?.companyId) {
        throw new Error('User not in company');
      }
      
      // First, try to load from shared document content
      const docRef = doc(db, `companies/${profile.companyId}/sharedDocumentContent/${reportId}`);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const serializedData = serializeFirebaseDocument(data);
        
        const documentContent = {
          reportId: serializedData.reportId,
          pages: serializedData.pages || [''],
          lastModified: serializedData.lastModified || new Date(),
          userId: serializedData.userId
        };
        
        return documentContent;
      } else {
        // console.log('📄 No saved shared document content found, trying to find original...');
        
        // Fallback: Find the original report and load its document content
        const sharedReportsQuery = query(
          collection(db, 'companies', profile.companyId, 'shared_reports'),
          where('__name__', '==', reportId)
        );
        const sharedReportsSnap = await getDocs(sharedReportsQuery);
        
        if (!sharedReportsSnap.empty) {
          const sharedReportData = sharedReportsSnap.docs[0].data();
          const originalReportId = sharedReportData.original_report_id;
          const originalUserId = sharedReportData.original_owner_id;
          
          if (originalReportId && originalUserId) {
            // console.log('📄 Found original report info, loading content from original user...');
            const originalContent = await this.loadDocumentContent(originalReportId, originalUserId);
            
            if (originalContent) {
              // console.log('✅ Successfully loaded original document content as fallback with', originalContent.pages.length, 'pages');
              return originalContent;
            }
          }
        }
        
        // console.log('📄 No document content found for shared report:', reportId);
        return null;
      }
    } catch (error) {
      // console.error('❌ Error loading shared document content:', error);
      return null;
    }
  }

  // MARK: - User Profile Management
  static async fetchUserProfile(): Promise<UserProfile | null> {
    await this.ensureAuthenticated();
    const userId = auth.currentUser?.uid;
    const userEmail = auth.currentUser?.email;
    const displayName = auth.currentUser?.displayName;
    
    if (!userId || !userEmail) return null;

    try {
      // Fetch from users collection (unified for iOS & Web)
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        
        const profile = {
          id: userDoc.id,
          userId,
          email: userEmail,
          displayName: data.display_name || data.displayName || undefined,
          companyId: data.companyId || data.company_id || undefined,
          companyRole: (data.companyRole || data.company_role) as CompanyRole || undefined,
          companyName: data.companyName || data.company_name || undefined,
          joinedCompanyAt: data.joinedCompanyAt?.toDate() || data.joined_company_at?.toDate() || undefined,
          profileImageUrl: data.profile_image_url || data.profileImageUrl || undefined,
          profileImagePath: data.profile_image_path || data.profileImagePath || undefined,
          createdAt: data.created_at?.toDate() || data.createdAt?.toDate() || new Date(),
          updatedAt: data.updated_at?.toDate() || data.updatedAt?.toDate() || new Date()
        };
        
        return profile;
      } else {
        // Create new profile if it doesn't exist
        const profileData = {
          user_id: userId,
          email: userEmail,
          display_name: displayName || '',
          created_at: serverTimestamp(),
          updated_at: serverTimestamp()
        };
        
        await setDoc(doc(db, 'users', userId), profileData);
        
        const newProfile: UserProfile = {
          id: userId,
          userId,
          email: userEmail,
          displayName: displayName || undefined,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        return newProfile;
      }
    } catch (error: any) {
      console.error('🚨 Error fetching user profile:', error);
      
      // If it's a permissions error, attempt to create the profile
      if (error.code === 'permission-denied') {
        try {
          const profileData = {
            user_id: userId,
            email: userEmail,
            display_name: displayName || '',
            created_at: serverTimestamp(),
            updated_at: serverTimestamp()
          };
          
          await setDoc(doc(db, 'users', userId), profileData);
          await setDoc(doc(db, 'users', userId), profileData);
          
          //console.log('🎉 Successfully created user profile after permission error');
          
          return {
            id: userId,
            userId,
            email: userEmail,
            displayName: displayName || undefined,
            createdAt: new Date(),
            updatedAt: new Date()
          };
        } catch (createError) {
          console.error('🚨 Failed to create user profile:', createError);
        }
      }
      
      return null;
    }
  }

  static async getUserProfileById(userId: string): Promise<UserProfile | null> {
    try {
      //console.log(`🔍 getUserProfileById: Fetching profile for user: ${userId}`);
      
      let userDoc = await getDoc(doc(db, 'users', userId));
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        
        // Check if essential fields are missing, if so fall back to users collection
        if (!data.email && !data.display_name) {
        } else {
          const profile = {
            id: userDoc.id,
            userId,
            email: data.email || 'Unknown',
            displayName: data.display_name || data.displayName || undefined,
            companyId: data.company_id || undefined,
            companyRole: data.company_role as CompanyRole || undefined,
            companyName: data.company_name || undefined,
            joinedCompanyAt: data.joined_company_at?.toDate() || undefined,
            profileImageUrl: data.profile_image_url || data.profileImageUrl || undefined,
            profileImagePath: data.profile_image_path || undefined,
            createdAt: data.created_at?.toDate() || new Date(),
            updatedAt: data.updated_at?.toDate() || new Date()
          };
          
          return profile;
        }
      }
      
      // Fallback to users collection (web app structure)
      userDoc = await getDoc(doc(db, 'users', userId));
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        //console.log(`📄 users data for ${userId}:`, data);
        
        const profile = {
          id: userDoc.id,
          userId,
          email: data.email || 'Unknown',
          displayName: data.display_name || undefined,
          companyId: data.company_id || undefined,
          companyRole: data.company_role as CompanyRole || undefined,
          companyName: data.company_name || undefined,
          joinedCompanyAt: data.joined_company_at?.toDate() || undefined,
          profileImageUrl: data.profile_image_url || undefined,
          profileImagePath: data.profile_image_path || undefined,
          createdAt: data.created_at?.toDate() || new Date(),
          updatedAt: data.updated_at?.toDate() || new Date()
        };
        
        //console.log(`📄 getUserProfileById returning from users:`, profile);
        return profile;
      }
      
      //console.log(`❌ getUserProfileById: No profile found for user ${userId}`);
      return null;
    } catch (error) {
      console.error('Error fetching user profile by ID:', error);
      return null;
    }
  }

  static async createUserProfile(userId: string, email: string, displayName: string): Promise<void> {
    //console.log('🔥 createUserProfile called:', { userId, email, displayName });
    
    try {
      const profileData = {
        user_id: userId,
        email: email,
        display_name: displayName,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      };
      
      //console.log('🔥 Creating user profile with data:', profileData);
      
      // Create profile in users collection (unified for iOS & Web)
      await setDoc(doc(db, 'users', userId), profileData);
      
      //console.log('🔥 User profile created successfully');
    } catch (error) {
      console.error('🔥 Error creating user profile:', error);
      console.error('🔥 Error details:', JSON.stringify(error, null, 2));
      throw error;
    }
  }

  static async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
      //console.log('📧 Password reset email sent to:', email);
    } catch (error: any) {
      console.error('❌ Error sending password reset email:', error);
      
      // Provide user-friendly error messages
      if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email address.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many reset attempts. Please try again later.');
      } else {
        throw new Error('Failed to send password reset email. Please try again.');
      }
    }
  }

  static async updateUserProfile(profile: Partial<UserProfile>): Promise<void> {
    await this.ensureAuthenticated();
    const userId = auth.currentUser?.uid;
    
    if (!userId) throw new Error('User not authenticated');

    //console.log('🔄 updateUserProfile - Input profile data:', profile);

    const updateData: any = {
      updated_at: serverTimestamp()
    };

    if (profile.companyId !== undefined) updateData.company_id = profile.companyId;
    if (profile.companyRole !== undefined) updateData.company_role = profile.companyRole;
    if (profile.companyName !== undefined) updateData.company_name = profile.companyName;
    if (profile.joinedCompanyAt !== undefined) updateData.joined_company_at = Timestamp.fromDate(profile.joinedCompanyAt);
    if (profile.profileImageUrl !== undefined) updateData.profile_image_url = profile.profileImageUrl;
    if (profile.profileImagePath !== undefined) updateData.profile_image_path = profile.profileImagePath;

    //console.log('🔄 updateUserProfile - Firestore update data:', updateData);

    // Update both collections to maintain compatibility - use setDoc with merge to create if doesn't exist
    await setDoc(doc(db, 'users', userId), updateData, { merge: true });
    await setDoc(doc(db, 'users', userId), updateData, { merge: true });
    //console.log('✅ updateUserProfile - Profile updated in both Firestore collections');
  }

  // MARK: - Company Operations
  static async createCompany(name: string): Promise<Company> {
    await this.ensureAuthenticated();
    const userId = auth.currentUser?.uid;
    const userEmail = auth.currentUser?.email;
    
    //console.log('🔥 Creating company for user:', { userId, userEmail });
    
    if (!userId || !userEmail) throw new Error('User not authenticated');

    // IMPORTANT: Clear any existing company data before creating new company
    //console.log('🔥 Clearing any existing company data before creating new company...');
    
    // Ensure user profile exists first - this will create it if it doesn't exist
    //console.log('🔥 Fetching user profile before creating company...');
    const userProfile = await this.fetchUserProfile();
    //console.log('🔥 User profile:', userProfile);
    if (!userProfile) {
      throw new Error('Unable to create or fetch user profile');
    }
    
    // Make sure user profile is clean (no existing company association)
    if (userProfile.companyId) {
      //console.log('🔥 User has existing company association, cleaning up first...');
      await updateDoc(doc(db, 'users', userId), {
        company_id: null,
        company_role: null,
        company_name: null,
        joined_company_at: null,
        updated_at: serverTimestamp()
      });
      // Refresh profile
      await this.fetchUserProfile();
    }

    // Generate unique company code
    const companyCode = await this.generateUniqueCompanyCode();
    
    const ownerMember: CompanyMember = {
      userId,
      email: userEmail,
      role: CompanyRole.OWNER,
      joinedAt: new Date()
    };

    const company: Omit<Company, 'id'> = {
      name,
      code: companyCode,
      createdAt: new Date(),
      ownerId: userId,
      members: [ownerMember]
    };

    const companyData = {
      name: company.name,
      code: company.code,
      created_at: Timestamp.fromDate(company.createdAt),
      owner_id: company.ownerId,
      members: company.members.map(member => ({
        user_id: member.userId,
        email: member.email,
        role: member.role,
        joined_at: Timestamp.fromDate(member.joinedAt)
      }))
    };
    
    //console.log('🔥 Creating company with data:', JSON.stringify(companyData, null, 2));
    
    try {
      const docRef = await addDoc(collection(db, 'companies'), companyData);
      //console.log('🔥 Company document created with ID:', docRef.id);

      // Update user profile
      //console.log('🔥 Updating user profile with company info');
      await this.updateUserProfile({
        companyId: docRef.id,
        companyRole: CompanyRole.OWNER,
        companyName: name,
        joinedCompanyAt: new Date()
      });
      //console.log('🔥 User profile updated successfully');

      return {
        id: docRef.id,
        ...company
      };
    } catch (error) {
      console.error('🔥 Error in createCompany:', error);
      throw error;
    }
  }

  private static async generateUniqueCompanyCode(): Promise<string> {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    
    while (true) {
      const code = Array.from({ length: 6 }, () => 
        characters.charAt(Math.floor(Math.random() * characters.length))
      ).join('');
      
      const existingQuery = query(
        collection(db, 'companies'),
        where('code', '==', code)
      );
      
      const existingDocs = await getDocs(existingQuery);
      
      if (existingDocs.empty) {
        return code;
      }
    }
  }

  static async joinCompany(code: string): Promise<void> {
    //console.log('🔥 joinCompany called with code:', code);
    
    try {
      await this.ensureAuthenticated();
      const userId = auth.currentUser?.uid;
      const userEmail = auth.currentUser?.email;
      
      if (!userId || !userEmail) throw new Error('User not authenticated');

      // Find company by code
      //console.log('🔥 Searching for company with code:', code.toUpperCase());
      const companyQuery = query(
        collection(db, 'companies'),
        where('code', '==', code.toUpperCase())
      );
      
      const companyDocs = await getDocs(companyQuery);
      //console.log('🔥 Company search results:', companyDocs.size, 'documents found');
      
      if (companyDocs.empty) {
        throw new Error('Invalid company code');
      }

      const companyDoc = companyDocs.docs[0];
      const companyId = companyDoc.id;
      //console.log('🔥 Found company:', companyId);

      // Check if user already has a pending request for this specific company
      //console.log('🔥 Checking for existing requests in company subcollection...');
      const existingRequestQuery = query(
        collection(db, 'companies', companyId, 'join_requests'),
        where('user_id', '==', userId),
        where('status', '==', RequestStatus.PENDING)
      );
      
      const existingRequests = await getDocs(existingRequestQuery);
      //console.log('🔥 Existing requests found:', existingRequests.size);
      
      if (!existingRequests.empty) {
        throw new Error('You already have a pending request for this company');
      }

      // Create join request in the company's subcollection
      //console.log('🔥 Creating join request in companies/', companyId, '/join_requests...');
      const requestData = {
        company_code: code.toUpperCase(),
        company_id: companyId,
        user_id: userId,
        user_email: userEmail,
        status: RequestStatus.PENDING,
        requested_at: new Date()
      };
      //console.log('🔥 Request data:', requestData);
      
      await addDoc(collection(db, 'companies', companyId, 'join_requests'), requestData);
      //console.log('🔥 Join request created successfully');
    } catch (error) {
      console.error('🔥 Error in joinCompany:', error);
      throw error;
    }
  }

  // iOS-style company members - query users by companyId instead of reading from company.members array
  static async getCompanyMembers(companyId: string): Promise<CompanyMember[]> {
    await this.ensureAuthenticated();
    
    //console.log('🔍 getCompanyMembers (iOS-style): Fetching users with companyId:', companyId);
    
    try {
      // Query all users who have this companyId - this is how iOS does it
      const usersQuery = query(
        collection(db, 'users'),
        where('company_id', '==', companyId)
      );
      
      const usersSnapshot = await getDocs(usersQuery);
      //console.log('🔍 getCompanyMembers (iOS-style): Found', usersSnapshot.docs.length, 'users in company');
      
      const members: CompanyMember[] = [];
      
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        //console.log('🔍 Processing user:', userDoc.id, userData);
        
        const member: CompanyMember = {
          userId: userDoc.id,
          email: userData.email || 'Unknown',
          role: userData.company_role as CompanyRole || CompanyRole.MEMBER,
          joinedAt: userData.joined_company_at?.toDate() || new Date(),
          displayName: userData.display_name,
          profileImageUrl: userData.profile_image_url
        };
        
        members.push(member);
        //console.log('🔍 Added member:', member);
      }
      
      try {
        const userProfilesQuery = query(
          collection(db, 'users'),
          where('company_id', '==', companyId)
        );
        
        const profilesSnapshot = await getDocs(userProfilesQuery);
        
        for (const profileDoc of profilesSnapshot.docs) {
          const profileData = profileDoc.data();
          
          // Check if we already have this user from the users collection
          if (!members.find(m => m.userId === profileDoc.id)) {
            
            const member: CompanyMember = {
              userId: profileDoc.id,
              email: profileData.email || 'Unknown',
              role: profileData.company_role as CompanyRole || CompanyRole.MEMBER,
              joinedAt: profileData.joined_company_at?.toDate() || new Date(),
              displayName: profileData.display_name,
              profileImageUrl: profileData.profile_image_url
            };
            
            members.push(member);
          }
        }
      } catch (profileError) {
      }
      
      //console.log('✅ getCompanyMembers (iOS-style): Returning', members.length, 'total members:', members);
      return members;
      
    } catch (error) {
      console.error('❌ getCompanyMembers (iOS-style): Error:', error);
      throw error;
    }
  }  static async getCompanyDetails(companyId: string): Promise<Company> {
    await this.ensureAuthenticated();
    
    //console.log('🔥 Getting company details for ID:', companyId);
    try {
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      
      if (!companyDoc.exists()) {
        console.error('🔥 Company document does not exist for ID:', companyId);
        throw new Error('Company not found');
      }
      
      //console.log('🔥 Company document exists, data:', companyDoc.data());
      
      const data = companyDoc.data();
      
      return {
        id: companyDoc.id,
        name: data.name,
        code: data.code,
        createdAt: data.created_at?.toDate() || new Date(),
        ownerId: data.owner_id,
        members: (data.members || []).map((member: any) => ({
          userId: member.user_id,
          email: member.email,
          role: member.role as CompanyRole,
          joinedAt: member.joined_at?.toDate() || new Date()
        }))
      };
    } catch (error) {
      console.error('🔥 Error fetching company document:', error);
      throw error;
    }
  }

  static async updateCompanyName(companyId: string, newName: string): Promise<UserProfile | null> {
    await this.ensureAuthenticated();
    const currentUserId = auth.currentUser?.uid;
    
    if (!currentUserId) throw new Error('User not authenticated');
    
    // Verify user is company owner
    const profile = await this.fetchUserProfile();
    if (profile?.companyId !== companyId) {
      throw new Error('You can only update your own company');
    }
    if (profile.companyRole !== CompanyRole.OWNER) {
      throw new Error('Only company owners can rename the company');
    }
    
    try {
      //console.log('🔥 Updating company name from', profile.companyName, 'to', newName);
      
      // Update company document
      await updateDoc(doc(db, 'companies', companyId), {
        name: newName.trim(),
        updatedAt: serverTimestamp()
      });
      
      // Update company name in all user profiles for this company (users collection)
      const usersQuery = query(
        collection(db, 'users'),
        where('company_id', '==', companyId)
      );
      
      const usersSnapshot = await getDocs(usersQuery);
      const updatePromises = usersSnapshot.docs.map(userDoc => 
        updateDoc(userDoc.ref, { 
          company_name: newName.trim(),
          updated_at: serverTimestamp()
        })
      );

      try {
        const userProfilesQuery = query(
          collection(db, 'users'),
          where('companyId', '==', companyId)
        );
        
        const profilesSnapshot = await getDocs(userProfilesQuery);
        const profileUpdatePromises = profilesSnapshot.docs.map(profileDoc =>
          updateDoc(profileDoc.ref, { 
            companyName: newName.trim(),
            company_name: newName.trim(), // Also update web-style field name
            updatedAt: serverTimestamp()
          })
        );
        
        updatePromises.push(...profileUpdatePromises);
      } catch (error) {
      }
      
      await Promise.all(updatePromises);
      
      //console.log('✅ Company name updated successfully in all user profiles');
      
      // Refresh current user profile to get updated data (like iOS)
      //console.log('🔄 Refreshing user profile to return updated data...');
      const updatedProfile = await this.fetchUserProfile();
      //console.log('🔄 Updated profile:', updatedProfile);
      
      return updatedProfile;
      
    } catch (error) {
      console.error('❌ Error updating company name:', error);
      this.handleFirebaseError(error, 'update company name');
      return null;
    }
  }
  
  static async leaveCompany(): Promise<void> {
    await this.ensureAuthenticated();
    const userId = auth.currentUser?.uid;
    
    if (!userId) throw new Error('User not authenticated');

    //console.log('🔥 Starting leave company process for user:', userId);

    const profile = await this.fetchUserProfile();
    //console.log('🔥 User profile:', profile);
    
    if (!profile?.companyId) {
      throw new Error('You are not part of any company');
    }

    const companyDoc = await getDoc(doc(db, 'companies', profile.companyId));
    
    if (!companyDoc.exists()) {
      throw new Error('Company not found');
    }

    const companyData = companyDoc.data();
    //console.log('🔥 Company data:', companyData);
    //console.log('🔥 Current user ID:', userId);
    //console.log('🔥 Company owner ID:', companyData.owner_id);
    
    if (companyData.owner_id === userId) {
      throw new Error('Company owner cannot leave. Please transfer ownership or delete the company.');
    }

    // Remove user from company's members array by filtering out the leaving member
    try {
      //console.log('🔥 Removing user from company members array...');
      //console.log('🔥 Original members count:', companyData.members?.length || 0);
      
      // Filter out the leaving member from the members array
      const filteredMembers = (companyData.members || []).filter((member: any) => member.user_id !== userId);
      
      //console.log('🔥 Filtered members count:', filteredMembers.length);
      //console.log('🔥 Removing user ID:', userId);
      //console.log('🔥 Company ID:', profile.companyId);
      
      await updateDoc(doc(db, 'companies', profile.companyId), {
        members: filteredMembers
      });
      //console.log('🔥 Successfully updated company members array');
    } catch (error) {
      console.error('🔥 Error removing user from company:', error);
      throw new Error(`Failed to remove user from company: ${error}`);
    }

    // Update user profile to remove company association
    try {
      //console.log('🔥 Attempting to update user profile...');
      // Remove company info from both user collections for compatibility
      const profileUpdates = {
        company_id: null,
        company_role: null,
        company_name: null,
        joined_company_at: null,
        updated_at: serverTimestamp()
      };

      // Update users collection (web app)
      await setDoc(doc(db, 'users', userId), profileUpdates, { merge: true });
      //console.log('🔥 Successfully updated users collection');

      try {
        await setDoc(doc(db, 'users', userId), {
          company_id: null,
          company_role: null,
          company_name: null,
          joined_company_at: null,
          // Also set the alternate field names just in case
          companyId: null,
          companyRole: null,
          companyName: null,
          joinedCompanyAt: null,
          updated_at: serverTimestamp()
        }, { merge: true });
      } catch (error) {
      }
    } catch (error) {
      console.error('🔥 Error updating user profile:', error);
      throw error;
    }

    //console.log('🔥 User successfully removed from company');
  }

  static async approveJoinRequest(requestId: string, companyId: string): Promise<void> {
    await this.ensureAuthenticated();
    const userId = auth.currentUser?.uid;
    
    if (!userId) throw new Error('User not authenticated');

    // Get the join request from the subcollection
    const requestDoc = await getDoc(doc(db, 'companies', companyId, 'join_requests', requestId));
    
    if (!requestDoc.exists()) {
      throw new Error('Join request not found');
    }

    const requestData = requestDoc.data();
    
    // Get company data
    const companyDoc = await getDoc(doc(db, 'companies', companyId));
    
    if (!companyDoc.exists()) {
      throw new Error('Company not found');
    }

    const companyData = companyDoc.data();
    
    // Check if current user can approve requests
    const isAdmin = companyData.owner_id === userId || 
                   companyData.members.some((member: any) => 
                     member.user_id === userId && (member.role === CompanyRole.ADMIN || member.role === CompanyRole.OWNER)
                   );
    
    if (!isAdmin) {
      throw new Error('You do not have permission to approve requests');
    }

    // Add new member to company
    const newMember = {
      user_id: requestData.user_id,
      email: requestData.user_email,
      role: CompanyRole.MEMBER,
      joined_at: Timestamp.fromDate(new Date())
    };

    //console.log('🔥 Adding new member to company:', newMember);
    //console.log('🔥 Company ID:', companyDoc.id);
    //console.log('🔥 Current user ID:', userId);
    //console.log('🔥 Company owner ID:', companyData.owner_id);

    try {
      // Check if user is already a member to prevent duplicates
      const currentMembers = companyData.members || [];
      const existingMember = currentMembers.find((member: any) => member.user_id === requestData.user_id);
      
      if (existingMember) {
        //console.log('🔥 User is already a member of the company');
        throw new Error('User is already a member of this company');
      }
      
      // Add new member to the members array
      const updatedMembers = [...currentMembers, newMember];
      
      await updateDoc(doc(db, 'companies', companyDoc.id), {
        members: updatedMembers
      });
      //console.log('🔥 Successfully added member to company');
    } catch (error) {
      console.error('🔥 Error adding member to company:', error);
      throw error;
    }

    // Update user's profile (create if doesn't exist)
    try {
      //console.log('🔥 Updating user profile for:', requestData.user_id);
      
      const companyUpdateData = {
        company_id: companyDoc.id,
        company_role: CompanyRole.MEMBER,
        company_name: companyData.name,
        joined_company_at: serverTimestamp(),
        updated_at: serverTimestamp()
      };
      
      // Update users collection (web app structure)
      await setDoc(doc(db, 'users', requestData.user_id), companyUpdateData, { merge: true });
      
      await setDoc(doc(db, 'users', requestData.user_id), companyUpdateData, { merge: true });
      
      //console.log('🔥 Successfully updated user profile');
    } catch (error) {
      console.error('🔥 Error updating user profile:', error);
      throw error;
    }

    // Delete the join request since it's been approved
    try {
      //console.log('🔥 Deleting approved join request:', requestId);
      await deleteDoc(doc(db, 'companies', companyId, 'join_requests', requestId));
      //console.log('🔥 Successfully deleted join request');
    } catch (error) {
      console.error('🔥 Error deleting join request:', error);
      throw error;
    }
  }

  static async rejectJoinRequest(requestId: string, companyId: string): Promise<void> {
    await this.ensureAuthenticated();
    
    await updateDoc(doc(db, 'companies', companyId, 'join_requests', requestId), {
      status: RequestStatus.REJECTED
    });
  }

  static async cleanupDuplicateMembers(companyId: string): Promise<void> {
    await this.ensureAuthenticated();
    const currentUserId = auth.currentUser?.uid;
    
    if (!currentUserId) throw new Error('User not authenticated');

    // Get company data
    const companyDoc = await getDoc(doc(db, 'companies', companyId));
    if (!companyDoc.exists()) {
      throw new Error('Company not found');
    }

    const companyData = companyDoc.data();
    
    // Check if current user is owner
    if (companyData.owner_id !== currentUserId) {
      throw new Error('Only company owner can cleanup duplicates');
    }

    try {
      const members = companyData.members || [];
      //console.log('🔥 Original members count:', members.length);
      
      // Remove duplicates based on user_id
      const uniqueMembers = members.filter((member: any, index: number, self: any[]) => 
        index === self.findIndex((m: any) => m.user_id === member.user_id)
      );
      
      //console.log('🔥 Unique members count:', uniqueMembers.length);
      //console.log('🔥 Removed duplicates:', members.length - uniqueMembers.length);
      
      if (uniqueMembers.length !== members.length) {
        await updateDoc(doc(db, 'companies', companyId), {
          members: uniqueMembers
        });
        //console.log('🔥 Successfully cleaned up duplicate members');
      } else {
        //console.log('🔥 No duplicates found');
      }
    } catch (error) {
      console.error('🔥 Error cleaning up duplicates:', error);
      throw error;
    }
  }

  static async removeMemberFromCompany(memberUserId: string, companyId: string): Promise<void> {
    await this.ensureAuthenticated();
    const currentUserId = auth.currentUser?.uid;
    
    if (!currentUserId) throw new Error('User not authenticated');

    // Get company data
    const companyDoc = await getDoc(doc(db, 'companies', companyId));
    if (!companyDoc.exists()) {
      throw new Error('Company not found');
    }

    const companyData = companyDoc.data();
    
    // Check if current user can remove members (owner or admin)
    const isAdmin = companyData.owner_id === currentUserId || 
                   companyData.members.some((member: any) => 
                     member.user_id === currentUserId && (member.role === CompanyRole.ADMIN || member.role === CompanyRole.OWNER)
                   );
    
    if (!isAdmin) {
      throw new Error('You do not have permission to remove members');
    }

    // Don't allow removing the owner
    const memberToRemove = companyData.members.find((member: any) => member.user_id === memberUserId);
    if (memberToRemove?.role === CompanyRole.OWNER) {
      throw new Error('Cannot remove company owner');
    }

    try {
      // Remove member from company
      const updatedMembers = companyData.members.filter((member: any) => member.user_id !== memberUserId);
      
      await updateDoc(doc(db, 'companies', companyId), {
        members: updatedMembers
      });

      // Update user's profile to remove company association
      await updateDoc(doc(db, 'users', memberUserId), {
        company_id: null,
        company_role: null,
        company_name: null,
        joined_company_at: null,
        updated_at: serverTimestamp()
      });

      //console.log('🔥 Successfully removed member from company');
    } catch (error) {
      console.error('🔥 Error removing member from company:', error);
      throw error;
    }
  }

  static async deleteCompany(): Promise<void> {
    await this.ensureAuthenticated();
    const currentUserId = auth.currentUser?.uid;
    
    if (!currentUserId) throw new Error('User not authenticated');

    // Get user profile to find company
    const profile = await this.fetchUserProfile();
    if (!profile?.companyId) {
      throw new Error('User is not part of any company');
    }

    const companyId = profile.companyId;

    // Get company data to verify ownership
    const companyDoc = await getDoc(doc(db, 'companies', companyId));
    if (!companyDoc.exists()) {
      throw new Error('Company not found');
    }

    const companyData = companyDoc.data();
    
    // Check if current user is the owner
    //console.log('🔥 Delete company - Current user ID:', currentUserId);
    //console.log('🔥 Delete company - Company owner ID:', companyData.owner_id);
    
    if (companyData.owner_id !== currentUserId) {
      throw new Error('Only company owner can delete the company');
    }

    try {
      //console.log('🔥 Starting company deletion process (iOS approach)...');
      
      // Step 1: Delete the company document first (like iOS)
      //console.log('🔥 Deleting company document...');
      await deleteDoc(doc(db, 'companies', companyId));
      //console.log('🔥 Company document deleted successfully');

      // Step 2: Find all users with this company_id and remove their association (like iOS)
      //console.log('🔥 Finding and updating all users with company association...');
      
      // Query users collection for anyone with this company_id
      const usersQuery = query(
        collection(db, 'users'),
        where('company_id', '==', companyId)
      );
      
      const usersSnapshot = await getDocs(usersQuery);
      //console.log(`🔥 Found ${usersSnapshot.docs.length} users to update in users collection`);
      
      // Update users in batch
      const userUpdatePromises = usersSnapshot.docs.map(userDoc => 
        updateDoc(userDoc.ref, {
          company_id: null,
          company_role: null,
          company_name: null,
          joined_company_at: null,
          updated_at: serverTimestamp()
        })
      );

      try {
        // Query by companyId field (iOS style)
        const userProfilesQuery1 = query(
          collection(db, 'users'),
          where('companyId', '==', companyId)
        );
        
        // Query by company_id field (web style)  
        const userProfilesQuery2 = query(
          collection(db, 'users'),
          where('company_id', '==', companyId)
        );
        
        const [profilesSnapshot1, profilesSnapshot2] = await Promise.all([
          getDocs(userProfilesQuery1),
          getDocs(userProfilesQuery2)
        ]);
        
        // Combine results and deduplicate
        const allProfileDocs = new Map();
        profilesSnapshot1.docs.forEach(doc => allProfileDocs.set(doc.id, doc));
        profilesSnapshot2.docs.forEach(doc => allProfileDocs.set(doc.id, doc));
        
        
        const profileUpdatePromises = Array.from(allProfileDocs.values()).map(profileDoc =>
          updateDoc(profileDoc.ref, {
            companyId: null,           // iOS field name
            company_id: null,          // Web field name
            companyRole: null,         // iOS field name
            company_role: null,        // Web field name
            companyName: null,         // iOS field name
            company_name: null,        // Web field name
            joinedCompanyAt: null,     // iOS field name
            joined_company_at: null,   // Web field name
            updatedAt: serverTimestamp()
          })
        );
        
        userUpdatePromises.push(...profileUpdatePromises);
      } catch (error) {
      }

      // Execute all user updates
      await Promise.all(userUpdatePromises);
      //console.log('🔥 All user profiles updated successfully');

      //console.log('🔥 Successfully deleted company using iOS approach');
      
      // IMPORTANT: Force refresh user profile to clear any cached company data
      //console.log('🔥 Force refreshing user profile after company deletion...');
      await this.fetchUserProfile();
      
      // Double check: if we still have company data after refresh, something went wrong
      const refreshedProfile = await this.fetchUserProfile();
      if (refreshedProfile?.companyId) {
        console.warn('🔥 User still has company association after deletion, forcing cleanup...');
        // Try one more direct cleanup
        await updateDoc(doc(db, 'users', currentUserId), {
          company_id: null,
          company_role: null,
          company_name: null,
          joined_company_at: null,
          updated_at: serverTimestamp()
        });
        // One final refresh
        await this.fetchUserProfile();
      }
      
    } catch (error) {
      console.error('🔥 Error deleting company:', error);
      throw error;
    }
  }



  static async getPendingJoinRequests(companyId: string): Promise<CompanyJoinRequest[]> {
    //console.log('🔥 getPendingJoinRequests called for companyId:', companyId);
    
    await this.ensureAuthenticated();
    
    // Query the join_requests subcollection under the company document (matching iOS structure)
    const requestsQuery = query(
      collection(db, 'companies', companyId, 'join_requests'),
      where('status', '==', RequestStatus.PENDING)
    );

    //console.log('🔥 Querying join_requests subcollection for company:', companyId, 'with status:', RequestStatus.PENDING);
    
    const requestsDocs = await getDocs(requestsQuery);
    //console.log('🔥 Found', requestsDocs.docs.length, 'pending join requests');
    
    const requests = requestsDocs.docs.map(doc => {
      const data = doc.data();
      //console.log('🔥 Request document data:', data);
      
      return {
        id: doc.id,
        companyCode: data.companyCode || data.company_code,
        userId: data.userId || data.user_id,
        userEmail: data.userEmail || data.user_email,
        status: data.status as RequestStatus,
        requestedAt: data.requestedAt?.toDate() || data.requested_at?.toDate() || new Date()
      };
    });
    
    //console.log('🔥 Returning requests:', requests);
    return requests;
  }

  // MARK: - Report Sharing
  static async shareReportToCompany(reportId: string): Promise<void> {
    try {
      //console.log('Starting shareReportToCompany:', { reportId });
      
      await this.ensureAuthenticated();
      const userId = auth.currentUser?.uid;
      
      if (!userId) throw new Error('User not authenticated');
      //console.log('User authenticated:', userId);

      const profile = await this.fetchUserProfile();
      //console.log('User profile:', { companyId: profile?.companyId });
      
      if (!profile?.companyId) {
        throw new Error('You must be a member of a company to share reports. Please join or create a company first.');
      }

      // Verify user document has company_id field (required by Firestore rules)
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();
      //console.log('User document company_id field:', userData?.company_id);
      
      if (!userData?.company_id) {
        throw new Error('User profile is missing company association. Please rejoin your company.');
      }

      // Get the original report
      const reportDoc = await getDoc(doc(db, 'users', userId, 'reports', reportId));
      
      if (!reportDoc.exists()) {
        console.error('Report not found:', reportId);
        throw new Error('Report not found');
      }

      const reportData = reportDoc.data();
      //console.log('Original report data keys:', Object.keys(reportData));
      //console.log('Original report title:', reportData.title);
      //console.log('Original report entries array:', reportData.entries);
      
      // Create shared report
      const sharedReport = {
        title: reportData.title,
        created_at: reportData.created_at,
        updated_at: serverTimestamp(),
        entries: reportData.entries || [],
        original_report_id: reportId,
        original_owner_id: userId,
        shared_at: serverTimestamp(),
        shared_by: userId
      };

      //console.log('Shared report data keys:', Object.keys(sharedReport));
      //console.log('Attempting to write to subcollection:', `companies/${profile.companyId}/shared_reports`);

      const sharedReportRef = await addDoc(
        collection(db, 'companies', profile.companyId, 'shared_reports'),
        sharedReport
      );

      //console.log('Report shared successfully, reference:', sharedReportRef.id);

      // Copy entries to shared entries collection
      const entriesQuery = query(
        collection(db, 'users', userId, 'entries'),
        where('report_id', '==', reportId)
      );

      const entriesDocs = await getDocs(entriesQuery);
      //console.log(`Found ${entriesDocs.docs.length} entries to share`);
      
      for (const entryDoc of entriesDocs.docs) {
        const entryData = entryDoc.data();
        const sharedEntryData = {
          ...entryData,
          report_id: sharedReportRef.id
        };
        
        //console.log('Entry data keys:', Object.keys(entryData));
        //console.log('Entry title:', entryData.title);
        //console.log('Entry note:', entryData.note);
        //console.log('Shared entry data keys:', Object.keys(sharedEntryData));
        //console.log('Writing to shared_entries subcollection with original ID:', entryDoc.id);
        
        // Use setDoc with original entry ID to maintain ID consistency with entries array
        await setDoc(
          doc(db, 'companies', profile.companyId, 'shared_entries', entryDoc.id),
          sharedEntryData
        );
      }
      
      //console.log(`All ${entriesDocs.docs.length} entries copied successfully`);

      // Copy document content if it exists
      //console.log('Checking for document content to copy...');
      try {
        const documentContent = await this.loadDocumentContent(reportId, userId);
        if (documentContent) {
          //console.log('Found document content, copying to shared collection...');
          const sharedDocContentRef = doc(db, 'companies', profile.companyId, 'sharedDocumentContent', sharedReportRef.id);
          await setDoc(sharedDocContentRef, {
            reportId: sharedReportRef.id,
            pages: documentContent.pages,
            lastModified: serverTimestamp(),
            userId: userId,
            originalReportId: reportId,
            originalUserId: userId
          });
          //console.log('Document content copied to shared collection');
        } else {
          //console.log('No document content found for report:', reportId);
        }
      } catch (docError) {
        // Document content copying is optional - don't fail the whole operation
        //console.log('Could not copy document content (optional):', docError);
      }
      
      //console.log('Report sharing completed successfully');
    } catch (error: any) {
      // If error already has a user-friendly message, rethrow it
      if (error instanceof Error && (
        error.message.includes('member of a company') ||
        error.message.includes('not authenticated') ||
        error.message.includes('not found')
      )) {
        throw error;
      }
      // Otherwise, handle as Firebase error
      this.handleFirebaseError(error, 'share report');
    }
  }

  static async unshareReport(reportId: string): Promise<void> {
    try {
      await this.ensureAuthenticated();
      const userId = auth.currentUser?.uid;
      
      if (!userId) throw new Error('User not authenticated');

      const profile = await this.fetchUserProfile();
      
      if (!profile?.companyId) {
        throw new Error('You must be a member of a company to unshare reports.');
      }

      // Find the shared report
      const sharedReportsQuery = query(
        collection(db, 'companies', profile.companyId, 'shared_reports'),
        where('original_report_id', '==', reportId)
      );

      const sharedReportsDocs = await getDocs(sharedReportsQuery);
      
      if (sharedReportsDocs.empty) {
        throw new Error('Shared report not found');
      }

      const sharedReportDoc = sharedReportsDocs.docs[0];
      const sharedReportData = sharedReportDoc.data();
      
      // Only original owner can unshare
      if (sharedReportData.original_owner_id !== userId) {
        throw new Error('Only the original owner can unshare this report');
      }

      // Delete shared entries
      const sharedEntriesQuery = query(
        collection(db, 'companies', profile.companyId, 'shared_entries'),
        where('report_id', '==', sharedReportDoc.id)
      );

      const sharedEntriesDocs = await getDocs(sharedEntriesQuery);
      
      for (const entryDoc of sharedEntriesDocs.docs) {
        await deleteDoc(entryDoc.ref);
      }

      // Delete shared report
      await deleteDoc(sharedReportDoc.ref);
    } catch (error: any) {
      // If error already has a user-friendly message, rethrow it
      if (error instanceof Error && (
        error.message.includes('member of a company') ||
        error.message.includes('not authenticated') ||
        error.message.includes('not found') ||
        error.message.includes('original owner')
      )) {
        throw error;
      }
      // Otherwise, handle as Firebase error
      this.handleFirebaseError(error, 'unshare report');
    }
  }

  static async updateSharedReportEntries(sharedReportId: string): Promise<void> {
    try {
      await this.ensureAuthenticated();
      const userId = auth.currentUser?.uid;
      
      if (!userId) throw new Error('User not authenticated');

      const profile = await this.fetchUserProfile();
      
      if (!profile?.companyId) {
        throw new Error('You must be a member of a company to update shared reports.');
      }

      // Get the shared report to find the original report ID
      const sharedReportDoc = await getDoc(
        doc(db, 'companies', profile.companyId, 'shared_reports', sharedReportId)
      );

      if (!sharedReportDoc.exists()) {
        throw new Error('Shared report not found');
      }

      const sharedReportData = sharedReportDoc.data();
      const originalReportId = sharedReportData.original_report_id;
      const originalOwnerId = sharedReportData.original_owner_id;

      if (!originalReportId || !originalOwnerId) {
        throw new Error('Invalid shared report data');
      }

      //console.log(`Updating shared report ${sharedReportId} with entries from original report ${originalReportId}`);

      // Delete existing shared entries
      const existingEntriesQuery = query(
        collection(db, 'companies', profile.companyId, 'shared_entries'),
        where('report_id', '==', sharedReportId)
      );

      const existingEntriesDocs = await getDocs(existingEntriesQuery);
      //console.log(`Deleting ${existingEntriesDocs.docs.length} existing shared entries`);
      
      for (const entryDoc of existingEntriesDocs.docs) {
        await deleteDoc(entryDoc.ref);
      }

      // Copy entries from original report
      const entriesQuery = query(
        collection(db, 'users', originalOwnerId, 'entries'),
        where('report_id', '==', originalReportId)
      );

      const entriesDocs = await getDocs(entriesQuery);
      //console.log(`Found ${entriesDocs.docs.length} entries to copy from original report`);
      
      for (const entryDoc of entriesDocs.docs) {
        const entryData = entryDoc.data();
        const sharedEntryData = {
          ...entryData,
          report_id: sharedReportId
        };
        
        await addDoc(
          collection(db, 'companies', profile.companyId, 'shared_entries'),
          sharedEntryData
        );
      }
      
      //console.log(`Successfully copied ${entriesDocs.docs.length} entries to shared report`);
    } catch (error: any) {
      if (error instanceof Error && (
        error.message.includes('member of a company') ||
        error.message.includes('not authenticated') ||
        error.message.includes('not found')
      )) {
        throw error;
      }
      this.handleFirebaseError(error, 'update shared report entries');
    }
  }

  static async getSharedReports(): Promise<SharedReport[]> {
    try {
      await this.ensureAuthenticated();
      
      const profile = await this.fetchUserProfile();
      
      if (!profile?.companyId) {
        //console.log('No company ID found in user profile:', profile);
        return [];
      }

      //console.log('Fetching shared reports for company:', profile.companyId);
      /*console.log('Current user profile:', { 
        uid: auth.currentUser?.uid, 
        email: auth.currentUser?.email, 
        companyId: profile.companyId 
      });*/

      // First, let's verify the user document structure
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user');
      }

      // Check the user's document in Firestore to ensure company_id is properly set
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (!userDocSnap.exists()) {
        console.error('User document does not exist in Firestore');
        return [];
      }

      const userData = userDocSnap.data();
      //console.log('User document data:', userData);
      
      if (!userData.company_id) {
        console.error('User document missing company_id field');
        return [];
      }

      if (userData.company_id !== profile.companyId) {
        console.error('Company ID mismatch:', { 
          profileCompanyId: profile.companyId, 
          userDocCompanyId: userData.company_id 
        });
      }

      const sharedReportsQuery = query(
        collection(db, 'companies', profile.companyId, 'shared_reports'),
        orderBy('shared_at', 'desc')
      );

      const sharedReportsDocs = await getDocs(sharedReportsQuery);
      //console.log('Found', sharedReportsDocs.docs.length, 'shared reports');
    
    // Create array of reports with sharer email information
    const reportsWithSharerInfo = await Promise.all(
      sharedReportsDocs.docs.map(async (doc) => {
        const data = doc.data();
        //console.log(`Processing shared report ${doc.id}:`, { title: data.title, dataKeys: Object.keys(data) });
        //console.log(`Original report ID: ${data.original_report_id}, Original owner: ${data.original_owner_id}`);
        
        // Get actual entry count from shared_entries collection using SHARED report ID (matching how entries were stored)
        const sharedReportId = doc.id; // Use the shared report ID, not the original
        const entriesQuery = query(
          collection(db, 'companies', profile.companyId!, 'shared_entries'),
          where('report_id', '==', sharedReportId)
        );
        const entriesDocs = await getDocs(entriesQuery);
        //console.log(`Found ${entriesDocs.docs.length} entries for SHARED report ${sharedReportId} (original: ${data.original_report_id})`);
        
        // Just use the entries that are actually in shared_entries collection (like iOS)
        const actualEntries = entriesDocs.docs.map(entryDoc => entryDoc.id);
        //console.log(`Using ${actualEntries.length} actual shared entries (no auto-sync)`);
        
        // If no entries found, log this but don't try to auto-sync (iOS doesn't do this)
        if (actualEntries.length === 0) {
          //console.log(`⚠️ No shared entries found for shared report ${sharedReportId}. Original report: ${data.original_report_id}, Owner: ${data.original_owner_id}`);
        }
        
        // Get sharer's profile to get display name, email, and profile image
        let sharerDisplayName = 'Unknown User';
        let sharerEmail = '';
        let sharerProfileImageUrl = '';
        let sharerInitials = '';
        
        //console.log(`🔍 Looking up sharer profile for user: ${data.shared_by}`);
        if (data.shared_by) {
          try {
            const sharerProfile = await this.getUserProfileById(data.shared_by);
            //console.log(`📄 Sharer profile found:`, sharerProfile);
            sharerDisplayName = sharerProfile?.displayName || sharerProfile?.email || 'Unknown User';
            sharerEmail = sharerProfile?.email || '';
            sharerProfileImageUrl = sharerProfile?.profileImageUrl || '';
            //console.log(`👤 Sharer details: name="${sharerDisplayName}", email="${sharerEmail}"`);
            
            // Generate initials from display name or email
            if (sharerProfile?.displayName) {
              sharerInitials = sharerProfile.displayName.split(' ').map(n => n[0]).join('').toUpperCase();
            } else if (sharerProfile?.email) {
              sharerInitials = sharerProfile.email[0].toUpperCase();
            } else {
              sharerInitials = 'U';
            }
          } catch (error) {
            //console.log('Could not fetch sharer profile:', error);
          }
        }
        
        return {
          id: doc.id,
          title: data.title,
          createdAt: data.created_at?.toDate() || new Date(),
          updatedAt: data.updated_at?.toDate() || new Date(),
          entries: actualEntries,
          originalReportId: data.original_report_id || '',
          originalOwnerId: data.original_owner_id || '',
          sharedAt: data.shared_at?.toDate() || new Date(),
          sharedBy: data.shared_by || '',
          sharerDisplayName: sharerDisplayName,
          sharerEmail: sharerEmail,
          sharerProfileImageUrl: sharerProfileImageUrl,
          sharerInitials: sharerInitials
        };
      })
    );
    
    return reportsWithSharerInfo;
    } catch (error: any) {
      console.error('Error fetching shared reports:', error);
      // Return empty array on permission errors to avoid breaking the UI
      if (error.code === 'permission-denied' || error.message?.includes('permission-denied')) {
        console.warn('Permission denied when fetching shared reports, returning empty array');
        return [];
      }
      this.handleFirebaseError(error, 'fetch shared reports');
      return [];
    }
  }

  static async fetchSharedEntries(reportId: string): Promise<PropertyEntryModel[]> {
    try {
      await this.ensureAuthenticated();
      
      const profile = await this.fetchUserProfile();
      if (!profile?.companyId) {
        throw new Error('You must be a member of a company to view shared entries.');
      }

      //console.log('🔍 fetchSharedEntries: Looking for entries for shared report:', reportId);
      //console.log('🔍 Company ID:', profile.companyId);
      
      // Search for entries using the shared report ID (which was assigned during sharing)
      const entriesQuery = query(
        collection(db, 'companies', profile.companyId, 'shared_entries'),
        where('report_id', '==', reportId)
      );

      const entriesDocs = await getDocs(entriesQuery);
      //console.log(`✅ Found ${entriesDocs.docs.length} shared entries for shared report ${reportId}`);
    
    const entries = entriesDocs.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title || '',
        note: data.note || '',
        createdAt: data.created_at?.toDate() || new Date(),
        reportId: data.report_id || '',
        latitude: data.latitude || 0,
        longitude: data.longitude || 0,
        address: data.address || '',
        images: data.images ? data.images.map((img: any) => ({
          ...img,
          photoURL: img.photo_url || img.photoURL // Normalize field name
        })) : [],
        // Legacy fields for backward compatibility
        photoURL: data.photo_url,
        imageMetadata: data.image_metadata ? {
          capturedAt: data.image_metadata.captured_at?.toDate() || new Date(),
          latitude: data.image_metadata.latitude,
          longitude: data.image_metadata.longitude,
          address: data.image_metadata.address
        } : undefined
      };
    });

    // Sort entries by created_at in descending order (newest first)
    return entries.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error: any) {
      // If error already has a user-friendly message, rethrow it
      if (error instanceof Error && error.message.includes('member of a company')) {
        throw error;
      }
      this.handleFirebaseError(error, 'fetch shared entries');
    }
  }

  static async canEditSharedReport(reportId: string): Promise<boolean> {
    await this.ensureAuthenticated();
    
    const profile = await this.fetchUserProfile();
    
    if (!profile?.companyId) {
      return false;
    }

    // Check if shared report exists in company
    const sharedReportDoc = await getDoc(
      doc(db, 'companies', profile.companyId, 'shared_reports', reportId)
    );

    return sharedReportDoc.exists();
  }

  static async isReportShared(reportId: string): Promise<boolean> {
    try {
      await this.ensureAuthenticated();
      const userId = auth.currentUser?.uid;
      
      if (!userId) return false;

      const profile = await this.fetchUserProfile();
      
      if (!profile?.companyId) {
        return false;
      }

      const sharedReportsQuery = query(
        collection(db, 'companies', profile.companyId, 'shared_reports'),
        where('original_report_id', '==', reportId)
      );

      const sharedReportsDocs = await getDocs(sharedReportsQuery);
      
      return !sharedReportsDocs.empty;
    } catch (error: any) {
      // If permission denied, assume not shared (will fail on share if actually shared)
      //console.log('Could not check if report is shared:', error);
      return false;
    }
  }

  static async getSharedReportInfo(reportId: string): Promise<SharingInfo | null> {
    try {
      await this.ensureAuthenticated();
      const userId = auth.currentUser?.uid;
      
      if (!userId) return null;

      const profile = await this.fetchUserProfile();
      
      if (!profile?.companyId) {
        return null;
      }

      const sharedReportsQuery = query(
        collection(db, 'companies', profile.companyId, 'shared_reports'),
        where('original_report_id', '==', reportId)
      );

      const sharedReportsDocs = await getDocs(sharedReportsQuery);
      
      if (sharedReportsDocs.empty) {
        return null;
      }

      const data = sharedReportsDocs.docs[0].data();
      
      return {
        sharedBy: data.shared_by,
        sharedAt: data.shared_at?.toDate() || new Date(),
        canUnshare: data.original_owner_id === userId
      };
    } catch (error: any) {
      // If permission denied, return null
      //console.log('Could not get shared report info:', error);
      return null;
    }
  }

  // MARK: - Real-time Subscriptions
  static subscribeToSharedReports(
    callback: (reports: PropertyReportModel[]) => void
  ): Unsubscribe | null {
    const userId = auth.currentUser?.uid;
    if (!userId) return null;

    // This is a simplified version - in practice you'd need to get the companyId first
    // For now, return null and implement when user profile is available
    return null;
  }

  static subscribeToCompanyMembers(
    companyId: string,
    callback: (members: CompanyMember[]) => void
  ): Unsubscribe {
    const companyRef = doc(db, 'companies', companyId);
    
    return onSnapshot(companyRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const members = (data.members || []).map((member: any) => ({
          userId: member.user_id,
          email: member.email,
          role: member.role as CompanyRole,
          joinedAt: member.joined_at?.toDate() || new Date()
        }));
        callback(members);
      }
    });
  }

  static subscribeToJoinRequests(
    companyId: string,
    callback: (requests: CompanyJoinRequest[]) => void
  ): Unsubscribe {
    // First get company code, then subscribe to requests
    getDoc(doc(db, 'companies', companyId)).then(companyDoc => {
      if (companyDoc.exists()) {
        const companyData = companyDoc.data();
        
        const requestsQuery = query(
          collection(db, 'company_join_requests'),
          where('company_code', '==', companyData.code),
          where('status', '==', RequestStatus.PENDING),
          orderBy('requested_at', 'desc')
        );

        onSnapshot(requestsQuery, (snapshot) => {
          const requests = snapshot.docs.map(doc => ({
            id: doc.id,
            companyCode: doc.data().company_code,
            userId: doc.data().user_id,
            userEmail: doc.data().user_email,
            status: doc.data().status as RequestStatus,
            requestedAt: doc.data().requested_at?.toDate() || new Date()
          }));
          
          callback(requests);
        });
      }
    });

    // Return a dummy unsubscribe function for now
    return () => {};
  }

  // ========================================
  // PROFILE IMAGE MANAGEMENT
  // ========================================

  /**
   * Upload profile image file to Firebase Storage
   */
  static async uploadProfileImage(file: File): Promise<{ url: string; path: string }> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      // Validate file size (5MB limit to match mobile app)
      const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSizeInBytes) {
        throw new Error('File size must be less than 5MB');
      }

      // Create storage path matching iOS app structure
      const fileName = 'profile.jpg';
      const storagePath = `users/${user.uid}/profile/${fileName}`;
      
      //console.log('📤 Uploading profile image to:', storagePath);

      const storage = getStorage();
      const storageRef = ref(storage, storagePath);
      
      // Upload the file
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      //console.log('✅ Profile image uploaded successfully:', downloadURL);
      
      return {
        url: downloadURL,
        path: storagePath
      };
    } catch (error) {
      console.error('❌ Error uploading profile image:', error);
      throw error;
    }
  }

  /**
   * Update user profile with new profile image
   */
  static async updateUserProfileImage(file: File): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // Upload the image
      const { url, path } = await this.uploadProfileImage(file);

      // Get current user profile
      const userProfile = await this.fetchUserProfile();
      if (!userProfile) {
        throw new Error('User profile not found');
      }

      // Delete old profile image if it exists
      if (userProfile.profileImagePath) {
        try {
          const storage = getStorage();
          const oldImageRef = ref(storage, userProfile.profileImagePath);
          await deleteObject(oldImageRef);
          //console.log('🗑️ Deleted old profile image');
        } catch (error) {
          console.warn('⚠️ Could not delete old profile image:', error);
          // Continue anyway - not critical
        }
      }

      // Update user profile with new image
      await this.updateUserProfile({
        profileImageUrl: url,
        profileImagePath: path,
        updatedAt: new Date()
      });
      //console.log('✅ User profile updated with new image');
    } catch (error) {
      console.error('❌ Error updating user profile image:', error);
      throw error;
    }
  }

  // Fix method for users whose profiles are out of sync between collections
  static async fixUserProfileSync(userId: string): Promise<void> {
    //console.log(`🔧 Fixing profile sync for user: ${userId}`);
    
    try {
      // Get data from users collection (web app structure)
      const usersDoc = await getDoc(doc(db, 'users', userId));
      
      if (usersDoc.exists()) {
        const usersData = usersDoc.data();
        //console.log(`📄 Found users data:`, usersData);
        
        if (usersData.company_id) {
          const syncData = {
            company_id: usersData.company_id,
            company_role: usersData.company_role,
            company_name: usersData.company_name,
            joined_company_at: usersData.joined_company_at,
            updated_at: serverTimestamp()
          };
          
          await updateDoc(doc(db, 'users', userId), syncData);
          //console.log(`✅ Profile sync completed for user: ${userId}`);
        } else {
          //console.log(`⚠️ No company data found in users collection for user: ${userId}`);
        }
      } else {
        //console.log(`❌ No users document found for user: ${userId}`);
      }
    } catch (error) {
      console.error(`❌ Error fixing profile sync for user ${userId}:`, error);
      throw error;
    }
  }
}
